import { useState } from "react";
import {
  useGetImagesQuery,
  useUploadImageMutation,
  useDeleteImageMutation,
  type Image,
} from "../../features/images/imageApi";
import Table from "../../components/list/Table";
import Pagination from "../../components/list/Pagination";
import type { PaginationState } from "@tanstack/react-table";
import SlideInModal from "../../components/modals/SlideInModal";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import FormInput from "../../components/forms/FormInput";
import { FiPlus, FiTrash2, FiUpload, FiX, FiCopy, FiFile } from "react-icons/fi";
import GridLoader from "react-spinners/GridLoader";
import { toast } from "react-toastify";

const filterFormLabelClass = "text-sm font-semibold text-gray-800 mb-2 block";
const filterFormErrorClass = "text-rose-500 text-xs mt-1.5";
const filterFormInputClass =
  "w-full py-2.5 px-4 border border-gray-200 rounded-lg text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-[#dbb685] focus:border-[#dbb685] transition-all shadow-sm hover:border-gray-300";

const imageSchema = yup.object({
  title: yup.string().required("Title is required"),
});

type ImageFormData = yup.InferType<typeof imageSchema>;

const Images = () => {
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [imageToDelete, setImageToDelete] = useState<Image | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const { data: imagesResponse, isLoading, isFetching } = useGetImagesQuery();
  const [uploadImageMutation] = useUploadImageMutation();
  const [deleteImageMutation] = useDeleteImageMutation();

  const images = imagesResponse?.data || [];

  const methods = useForm<ImageFormData>({
    resolver: yupResolver(imageSchema) as any,
    defaultValues: {
      title: "",
    },
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = methods;

  const handleOpenModal = () => {
    reset({
      title: "",
    });
    setSelectedFile(null);
    setImagePreview(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedFile(null);
    setImagePreview(null);
    reset();
  };

  // Handle file selection
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type - accept images and PDFs
      const isValidType = file.type.startsWith("image/") || file.type === "application/pdf";
      if (!isValidType) {
        toast.error("Please select an image or PDF file");
        return;
      }
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error("File size must be less than 5MB");
        return;
      }
      setSelectedFile(file);
      // Create preview from selected file (only for images)
      if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setImagePreview(reader.result as string);
        };
        reader.readAsDataURL(file);
      } else {
        // For PDFs, don't set preview
        setImagePreview(null);
      }
    }
  };

  const onSubmit = async (data: ImageFormData) => {
    if (!selectedFile) {
      toast.error("Please select a file");
      return;
    }

    try {
      setIsUploading(true);
      const formData = new FormData();
      formData.append("image", selectedFile);
      formData.append("title", data.title);

      await uploadImageMutation(formData).unwrap();
      toast.success("File uploaded successfully");
      handleCloseModal();
    } catch (error) {
      console.error("Error uploading file:", error);
      toast.error("Failed to upload file. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteClick = (image: Image) => {
    setImageToDelete(image);
    setShowDeleteConfirm(true);
  };

  const handleDelete = async () => {
    if (!imageToDelete?.image_id) return;

    try {
      setIsDeleting(true);
      await deleteImageMutation(imageToDelete.image_id).unwrap();
      toast.success("File deleted successfully");
      setShowDeleteConfirm(false);
      setImageToDelete(null);
    } catch (error) {
      console.error("Error deleting file:", error);
      toast.error("Failed to delete file. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  const renderTextCell = (info: any) => {
    return (
      <span className="text-gray-600 font-medium">
        {info.getValue() || <span className="text-gray-400">-</span>}
      </span>
    );
  };

  const renderImageCell = (info: any) => {
    const imageLink = info.getValue();
    if (!imageLink) {
      return <span className="text-gray-400">-</span>;
    }
    // Check if it's a PDF by file extension or URL
    const isPdf = imageLink.toLowerCase().endsWith('.pdf') || imageLink.includes('application/pdf');
    
    if (isPdf) {
      return (
        <div className="flex justify-center">
          <a
            href={imageLink}
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-col items-center justify-center h-16 w-16 rounded-lg border border-gray-200 bg-red-50 hover:bg-red-100 transition-colors"
            title="View PDF"
          >
            <FiFile className="text-red-600" size={24} />
            <span className="text-xs text-red-600 font-medium mt-1">PDF</span>
          </a>
        </div>
      );
    }
    
    return (
      <div className="flex justify-center">
        <img
          src={imageLink}
          alt="Image"
          className="h-16 w-16 object-cover rounded-lg border border-gray-200"
          onError={(e) => {
            // If image fails to load, hide it and show a placeholder
            const target = e.target as HTMLImageElement;
            target.style.display = 'none';
            // Show a file icon as fallback
            const parent = target.parentElement;
            if (parent && !parent.querySelector('a')) {
              const link = document.createElement('a');
              link.href = imageLink;
              link.target = '_blank';
              link.rel = 'noopener noreferrer';
              link.className = 'flex flex-col items-center justify-center h-16 w-16 rounded-lg border border-gray-200 bg-gray-50 hover:bg-gray-100 transition-colors';
              link.title = 'View file';
              link.innerHTML = `
                <svg class="text-gray-600" width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                </svg>
                <span class="text-xs text-gray-600 font-medium mt-1">File</span>
              `;
              parent.appendChild(link);
            }
          }}
        />
      </div>
    );
  };

  const handleCopyLink = async (link: string) => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Link copied to clipboard!");
    } catch (error) {
      console.error("Failed to copy:", error);
      toast.error("Failed to copy link");
    }
  };

  const renderLinkCell = (info: any) => {
    const link = info.getValue();
    if (!link) {
      return <span className="text-gray-400">-</span>;
    }
    return (
      <div className="max-w-[400px] overflow-hidden">
        <a
          href={link}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800 hover:underline text-sm font-medium block truncate"
          title={link}
        >
          {link}
        </a>
      </div>
    );
  };

  const renderActions = ({ row }: any) => {
    const image = row.original as Image;
    const link = image.image_link;
    return (
      <div className="flex justify-center items-center gap-1.5">
        {link && (
          <button
            className="px-2 py-1.5 rounded-lg transition-colors flex items-center justify-center gap-1.5 text-blue-600 hover:bg-blue-50 cursor-pointer border border-blue-200 bg-blue-50/50"
            onClick={() => handleCopyLink(link)}
            title="Copy link"
          >
            <FiCopy size={14} />
            <span className="text-xs font-medium">Copy</span>
          </button>
        )}
        <button
          className="px-2 py-1.5 rounded-lg transition-colors flex items-center justify-center gap-1.5 text-rose-600 hover:bg-rose-50 cursor-pointer border border-rose-200 bg-rose-50/50"
          onClick={() => handleDeleteClick(image)}
          title="Delete"
        >
          <FiTrash2 size={14} />
          <span className="text-xs font-medium">Delete</span>
        </button>
      </div>
    );
  };

  const columns = [
    {
      id: "preview",
      header: "Preview",
      accessorKey: "image_link",
      cell: renderImageCell,
      meta: { align: 'center' }
    },
    {
      id: "title",
      header: "Title",
      accessorKey: "title",
      cell: renderTextCell,
      meta: { align: 'left' }
    },
    {
      id: "link",
      header: "Link",
      accessorKey: "image_link",
      cell: renderLinkCell,
      meta: { align: 'left' },
    },
    {
      id: "actions",
      header: "Actions",
      cell: renderActions,
      meta: { align: 'center' }
    },
  ];

  return (
    <>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-xl font-semibold text-gray-600 mb-1">Images</h1>
          </div>
          <button
            onClick={handleOpenModal}
            className="px-4 py-2 text-white rounded-lg font-semibold flex items-center justify-center gap-2 text-sm cursor-pointer"
            style={{
              minWidth: '130px',
              minHeight: '38px',
              backgroundColor: '#dbb685',
              transition: 'background-color 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#c9a570';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#dbb685';
            }}
          >
            <FiPlus size={16} />
            <span>Add Image</span>
          </button>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {isLoading ? (
            <div className="flex justify-center items-center h-64">
              <GridLoader color="#dbb685" />
            </div>
          ) : (
            <>
              <Table
                columns={columns}
                data={images}
                isSearchable={true}
                isSortable={true}
                isFetching={isFetching}
                pagination={true}
                paginationState={pagination}
                setPaginationState={setPagination}
                tableClass="w-full"
                tdClass="text-sm py-4 px-6 text-gray-700 font-medium"
                theadRowClass="bg-gray-100 border-b border-gray-200"
              />
              <div className="mt-6 flex justify-between items-center bg-white rounded-lg border border-gray-200 p-4 mx-4 mb-4">
                <div className="text-sm text-gray-600">
                  Showing{" "}
                  <span className="font-semibold text-gray-800">
                    {(pagination.pageIndex * pagination.pageSize) + 1}
                  </span>{" "}
                  to{" "}
                  <span className="font-semibold text-gray-800">
                    {Math.min(
                      (pagination.pageIndex + 1) * pagination.pageSize,
                      images.length
                    )}
                  </span>{" "}
                  of{" "}
                  <span className="font-semibold text-gray-800">
                    {images.length}
                  </span>{" "}
                  files
                </div>
                <Pagination
                  currentPage={pagination.pageIndex + 1}
                  totalItems={images.length}
                  itemsPerPage={pagination.pageSize}
                  onPageChange={(page: number) => {
                    setPagination({
                      ...pagination,
                      pageIndex: page - 1,
                    });
                  }}
                  btnClass="px-4 py-2 border border-gray-300 text-sm text-gray-700 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all font-medium"
                  pageNumClass="text-sm px-3 py-1"
                  activePageNumClass="text-white rounded-lg font-semibold"
                  activePageNumStyle={{ backgroundColor: "#dbb685" }}
                  inactivePageNumClass="hover:bg-gray-100 rounded-lg text-gray-700 font-medium"
                />
              </div>
            </>
          )}
        </div>
      </div>

      <SlideInModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title="Add Image"
        width="600px"
      >
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <FormInput
              label="Title"
              name="title"
              placeholder="Enter image title"
              register={register}
              errors={errors}
              labelClassName={filterFormLabelClass}
              className={filterFormInputClass}
              errorClassName={filterFormErrorClass}
            />

            <div>
              <label className={filterFormLabelClass}>
                File (Image or PDF) <span className="text-red-500">*</span>
              </label>

              {!selectedFile ? (
                <div className="relative">
                  <label
                    htmlFor="image-upload"
                    className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 hover:border-[#dbb685] transition-colors group"
                  >
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      <FiUpload className="w-10 h-10 mb-3 text-gray-400 group-hover:text-[#dbb685] transition-colors" />
                      <p className="mb-2 text-sm text-gray-500">
                        <span className="font-semibold text-[#dbb685]">Click to upload</span> or drag and drop
                      </p>
                      <p className="text-xs text-gray-400">Images and PDF files supported</p>
                    </div>
                    <input
                      id="image-upload"
                      type="file"
                      accept="image/*,application/pdf"
                      onChange={handleFileSelect}
                      className="hidden"
                      required
                    />
                  </label>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center gap-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                    {imagePreview ? (
                      <div className="flex-shrink-0">
                        <img
                          src={imagePreview}
                          alt="Preview"
                          className="h-20 w-20 object-cover rounded-lg border border-gray-200"
                          onError={() => {
                            setImagePreview(null);
                          }}
                        />
                      </div>
                    ) : selectedFile.type === "application/pdf" ? (
                      <div className="flex-shrink-0 flex items-center justify-center h-20 w-20 rounded-lg border border-gray-200 bg-red-50">
                        <FiFile className="text-red-600" size={32} />
                      </div>
                    ) : null}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {selectedFile.name}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {(selectedFile.size / 1024).toFixed(2)} KB
                        {selectedFile.type === "application/pdf" && (
                          <span className="ml-2 px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs font-medium">
                            PDF
                          </span>
                        )}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedFile(null);
                        setImagePreview(null);
                        const fileInput = document.getElementById('image-upload') as HTMLInputElement;
                        if (fileInput) fileInput.value = '';
                      }}
                      className="flex-shrink-0 p-2 text-gray-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
                      title="Remove file"
                    >
                      <FiX size={18} />
                    </button>
                  </div>
                  <label
                    htmlFor="image-upload-change"
                    className="block text-center"
                  >
                    <span className="text-sm text-[#dbb685] hover:text-[#c9a570] cursor-pointer font-medium">
                      Choose a different file
                    </span>
                    <input
                      id="image-upload-change"
                      type="file"
                      accept="image/*,application/pdf"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                  </label>
                </div>
              )}
            </div>

            <div className="flex gap-3 justify-end pt-4 border-t border-gray-200 mt-6">
              <button
                type="button"
                onClick={handleCloseModal}
                className="px-6 py-2.5 text-sm font-semibold text-gray-700 bg-white rounded-lg shadow-sm flex items-center justify-center cursor-pointer"
                style={{
                  minWidth: '100px',
                  minHeight: '40px',
                  border: '2px solid #d1d5db',
                  transition: 'background-color 0.2s ease, border-color 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f9fafb';
                  e.currentTarget.style.borderColor = '#9ca3af';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#ffffff';
                  e.currentTarget.style.borderColor = '#d1d5db';
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isUploading || !selectedFile}
                className="px-6 py-2.5 text-sm font-semibold text-white rounded-lg shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                style={{
                  minWidth: '140px',
                  minHeight: '40px',
                  backgroundColor: '#dbb685',
                  transition: 'background-color 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  if (!isUploading && selectedFile) {
                    e.currentTarget.style.backgroundColor = '#c9a570';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isUploading && selectedFile) {
                    e.currentTarget.style.backgroundColor = '#dbb685';
                  }
                }}
              >
                {isUploading ? (
                  <span>Uploading...</span>
                ) : (
                  <span>Upload File</span>
                )}
              </button>
            </div>
          </form>
        </FormProvider>
      </SlideInModal>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && imageToDelete && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-[9998]"
            onClick={() => {
              setShowDeleteConfirm(false);
              setImageToDelete(null);
            }}
          ></div>
          <div className="fixed inset-0 flex items-center justify-center z-[9999]">
            <div className="bg-white rounded-lg border border-gray-200 shadow-xl p-6 max-w-md w-full mx-4">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center">
                  <FiTrash2 className="text-rose-600" size={20} />
                </div>
                <h3 className="text-lg font-bold text-gray-900">Delete File</h3>
              </div>
              <p className="text-sm text-gray-600 mb-1">
                Are you sure you want to delete the file{" "}
                <span className="font-semibold text-gray-900">"{imageToDelete.title}"</span>?
              </p>
              <p className="text-xs text-gray-500 mb-6">
                This action cannot be undone. The file will be permanently removed from the system.
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setImageToDelete(null);
                  }}
                  disabled={isDeleting}
                  className="px-6 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ minWidth: '100px', minHeight: '40px' }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={isDeleting}
                  className="px-6 py-2.5 text-sm font-semibold text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  style={{
                    minWidth: '100px',
                    minHeight: '40px',
                    backgroundColor: '#dc2626',
                    transition: 'background-color 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (!isDeleting) {
                      e.currentTarget.style.backgroundColor = '#b91c1c';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isDeleting) {
                      e.currentTarget.style.backgroundColor = '#dc2626';
                    }
                  }}
                >
                  {isDeleting ? (
                    <>
                      <GridLoader color="#ffffff" size={8} />
                      <span className="ml-2">Deleting...</span>
                    </>
                  ) : (
                    "Delete"
                  )}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default Images;

